import React from "react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

interface WorkflowNavigationProps {
  onBack?: () => void;
  onNext?: () => void;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  isBackLoading?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  showBackButton?: boolean;
  showNextButton?: boolean;
  customAction?: React.ReactNode;
  className?: string;
}

/**
 * WorkflowNavigation - Reusable navigation component for ScopingAI workflow steps
 * Positioned at the bottom of each step with proper spacing for CopilotKit
 */
export const WorkflowNavigation: React.FC<WorkflowNavigationProps> = ({
  onBack,
  onNext,
  isBackDisabled = false,
  isNextDisabled = false,
  isNextLoading = false,
  isBackLoading = false,
  nextButtonText = "Next",
  backButtonText = "Back",
  showBackButton = true,
  showNextButton = true,
  customAction,
  className = "",
}) => {
  return (
    <div className={`w-full workflow-navigation ${className}`}>
      {/* Navigation Container - Full width with no margins or padding */}
      <div className="relative bg-white border-t border-gray-200 shadow-sm">
        <div className="w-full">
          <div className="flex justify-between items-center w-full py-4 px-4">
            {/* Back Button - Positioned at start */}
            <div className="flex justify-start">
              {showBackButton && onBack ? (
                <Button
                  variant="outline"
                  onClick={onBack}
                  disabled={isBackDisabled || isBackLoading}
                  className="gap-2 min-w-[100px] shadow-sm hover:shadow-md transition-shadow"
                >
                  {isBackLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowLeft className="w-4 h-4" />
                  )}
                  {backButtonText}
                </Button>
              ) : (
                <div></div> // Empty div to maintain layout
              )}
            </div>

            {/* Custom Action - Center */}
            {customAction && (
              <div className="flex justify-center">
                {customAction}
              </div>
            )}

            {/* Next Button - Positioned at end */}
            <div className="flex justify-end">
              {showNextButton && onNext ? (
                <Button
                  onClick={onNext}
                  disabled={isNextDisabled || isNextLoading}
                  className="gap-2 min-w-[120px] shadow-sm hover:shadow-md transition-all duration-200 bg-primary hover:bg-primary/90"
                >
                  {nextButtonText}
                  {isNextLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : !isNextDisabled ? (
                    <ArrowRight className="w-4 h-4" />
                  ) : null}
                </Button>
              ) : (
                <div></div> // Empty div to maintain layout
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * WorkflowNavigationSpacer - Component to add bottom spacing to pages with navigation
 * Use this at the end of step content to ensure proper spacing for CopilotKit
 */
export const WorkflowNavigationSpacer: React.FC = () => {
  return <div className="h-24 w-full"></div>;
};

export default WorkflowNavigation;
